import { closestCorners, DndContext, DragEndEvent, DragOverEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, rectSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable'
import { faCalendar, faUser } from '@fortawesome/pro-light-svg-icons'
import { useMemo, useCallback, Fragment } from 'react'
import { flushSync } from 'react-dom'
import { CSS } from '@dnd-kit/utilities'
import { StatusFlowData, WorkSheetProduct, WorkSheetProductAssignedEmployeeTy } from './types'
import ModalEditProductStatusView from './ModalEditProductStatusView'
import { WorkSheetProductStatusModelType } from '@teinor/erp/types/company/order/sell/workSheet/workSheetProductStatus'
import TwinIcon from '../../../../../../baseComponents/TwinIcon'
import TwinToolTip from '../../../../../../baseComponents/TwinToolTip'
import twinFetchPost, { twinFetchPostJSON } from '../../../../../../utils/globals/data'
import { displayDateByString } from '../../../../../../utils/globals/date'
import { TwinDictionary } from '../../../../../../utils/globals/dictionary'
import useEditDeleteModal, { SetEditDeleteModalTy, UseEditDeleteModalProps } from '../../../../../../utils/hooks/useEditDeleteModal'
import { WorkSheetProductStatusFlowBaseLogicProps, WorkSheetProductStatusFlowBaseProps } from '..'
import ModalMatchProductWorkSheet from '../../ModalsWorkSheetLayout/ModalMatchProductWorkSheet'
import { getConfigParam, getEmployees } from '../../../../../../utils/reducers/getters'
import { ITDisplayImage } from '../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'
import ModalWorkSheetSellTag from '../../ModalsWorkSheetLayout/ModalWorkSheetSellTag'
import { displayFormatedNumber } from '../../../../../../utils/globals/numbers'
import { ChangeQuantityButton, PrintWorkSheetTagsButton } from '../../../OrderLayout/OrderTable/TableComponents/Inputs/WorkSheetProductStatusInput'
import ModalChangeQuantityWorkSheetProduct from '../../ModalsWorkSheetLayout/ModalChangeQuantityWorkSheetProduct'
import ModalPrintWorkSheetTags from '../../ModalsWorkSheetLayout/ModalPrintWorkSheetTags'
import { CustomSelectRoundImage } from '../../../../../../forms/CustomSelect/CustomSelectRoundImage'
import './workSheetProductStatusFlowInner.sass'

type WorkSheetProductStatusFlowInnerProps = WorkSheetProductStatusFlowBaseProps & WorkSheetProductStatusFlowInnerLogicProps & {
    showAllWorkSheets?: boolean
}

const WorkSheetProductStatusFlowInner: React.FC<WorkSheetProductStatusFlowInnerProps> = ({ readOnly, showAllWorkSheets = false, ...logic }) => {
    const renderThis: JSX.Element[] = []
    const { sensors, handleOnDragEnd, onHandleDragOver, openModal, setOpenModal, memorizedArrStatuses, memorizedGroup, onChangeAssignedEmployee } = useWorkSheetProductStatusFlowInnerLogic({ ...logic })
    if (!logic?.workSheetProductStatuses) {
        return null
    }
    if (logic.data.WorkSheetGroupStatusId) {
        for (const element of memorizedArrStatuses) {
            renderThis.push(
                <DroppableStatusColumn id={String(element?.id)} status={element} products={logic.data?.products?.[element?.id] || []} key={element?.id} {...{ setOpenModal, showAllWorkSheets, readOnly, onChangeAssignedEmployee }} />
            )
        }
    }
    return (
        <div className='flex flex-col flex-auto h-1'>
            <div className='grid gap-10 flex-auto mt-30 overflow-x-auto' style={{ gridTemplateColumns: `repeat(${renderThis.length}, minmax(260px, 290px))` }}>
                <DndContext
                    sensors={sensors}
                    onDragEnd={handleOnDragEnd}
                    onDragOver={onHandleDragOver}
                    collisionDetection={closestCorners}
                >
                    {renderThis}
                </DndContext>
            </div>
            {openModal?.type === 'create' ? <ModalEditProductStatusView onSubmit={() => { }} allRowData={openModal?.allRowData} setOpened={() => setOpenModal(null)} memorizedGroup={memorizedGroup} workSheetProductStatusURLs={logic.workSheetProductStatusURLs} getProducts={logic.getData} setOpenModal={setOpenModal} /> : null}
            {openModal?.type === 'delete' ? <ModalWorkSheetSellTag allRowData={openModal.allRowData} setOpenModal={setOpenModal} onSubmit={() => logic.getData()} /> : null}
            {openModal?.type === 'edit' && openModal.allRowData?.subType === 'changeQuantity' ? <ModalChangeQuantityWorkSheetProduct allRowData={openModal?.allRowData} setOpenModal={setOpenModal} getData={logic.getData} /> : null}
            {openModal?.type === 'edit' && openModal.allRowData?.subType === 'printTags' ? <ModalPrintWorkSheetTags setOpened={() => setOpenModal(null)} onSubmit={() => { }} allRowData={openModal?.allRowData} /> : null}
            {openModal?.type === 'edit' && !openModal.allRowData?.subType ? <ModalMatchProductWorkSheet groupProductByStatusURL={'/api/app/order/sell/workSheet/workSheetProduct/groupWorkSheetProduct'} allRowData={openModal.allRowData} setOpenModal={setOpenModal} getCompleteData={logic.getData} /> : null}
        </div>
    )
}

interface WorkSheetProductStatusFlowInnerLogicProps extends WorkSheetProductStatusFlowBaseLogicProps {
    WorkSheetId?: number
    setData: React.Dispatch<React.SetStateAction<StatusFlowData>>
    data: StatusFlowData
    getData: () => Promise<void>
}

const useWorkSheetProductStatusFlowInnerLogic = ({ workSheetProductStatusURLs, WorkSheetId, workSheetProductStatuses, data, setData, getData }: WorkSheetProductStatusFlowInnerLogicProps) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const memorizedStatuses = useMemo(() => workSheetProductStatuses?.dictWorkSheetGroupStatus?.[String(data.WorkSheetGroupStatusId)]?.WorkSheetProductStatusesDict || {} || {}, [workSheetProductStatuses, data.WorkSheetGroupStatusId])
    const memorizedArrStatuses = useMemo(() =>{
        return workSheetProductStatuses?.dictWorkSheetGroupStatus?.[String(data.WorkSheetGroupStatusId)]?.WorkSheetProductStatuses || []
    },[workSheetProductStatuses, data.WorkSheetGroupStatusId])
    const memorizedGroup = useMemo(() => workSheetProductStatuses?.dictWorkSheetGroupStatus?.[String(data.WorkSheetGroupStatusId)] || {} || {}, [workSheetProductStatuses, data.WorkSheetGroupStatusId])

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { delay: 200, tolerance: 5 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    )

    const onHandleDragOver = useCallback((event: DragOverEvent) => {
        const { active, over } = event
        const activeId = active.id
        const overId = parseInt(String(over?.id) || '')
        const activeContainer = parseInt(active?.data?.current?.sortable?.containerId)
        let overContainer = 0
        if (over?.data?.current && 'sortable' in over?.data?.current) {
            overContainer = parseInt(over?.data?.current?.sortable?.containerId) || parseInt(over?.data?.current?.columnId)
        } else {
            overContainer = parseInt(over?.data?.current?.columnId)
        }

        if (!activeContainer || !overContainer || activeContainer === overContainer) {
            return
        }
        setData((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            const copyProducts = copy.products
            const activeItems = copyProducts[activeContainer]
            const overItems = copyProducts[overContainer]

            if (!copyProducts[overContainer]) {
                copyProducts[overContainer] = []
            }

            const activeIndex = activeItems.findIndex(
                (item: any) => (item && item.id) && item.id === activeId
            )
            const overIndex = overItems?.findIndex((item: any) => (item && item.id) && item.id === overId);
            let newIndex: number;
            if (copyProducts && overId in copyProducts) {
                newIndex = (overItems?.length || 0) + 1
            } else {
                const isBelowOverItem =
                    over &&
                    active.rect.current.translated &&
                    active.rect.current.translated.top > over.rect.top + over.rect.height;
                const modifier = isBelowOverItem ? 1 : 0;
                newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
            }
            const myProducts = {
                ...copyProducts,
                [activeContainer]: copyProducts[activeContainer].filter(
                    (item: any) => (item && item.id ) && item.id !== active.id
                ),
                [overContainer]: [
                    ...copyProducts[overContainer].slice(0, newIndex),
                    copyProducts[activeContainer][activeIndex],
                    ...copyProducts[overContainer].slice(newIndex, copyProducts[overContainer].length)
                ]
            }
            return {
                ...copy,
               products: myProducts
            }
        })
    }, [setData])

    const handleOnDragEnd = useCallback(async (event: DragEndEvent) => {
        const { active, over } = event
        const activeId = active.id
        const overId = parseInt(String(over?.id) || '')
        const activeContainer = parseInt(active?.data?.current?.sortable?.containerId)
        let overContainer = parseInt(over?.data?.current?.sortable?.containerId)
        let worksheetProductWithStatus: any | null = null
        let productReference = ''
        if (!activeContainer || !overContainer || activeContainer !== overContainer) {
            return
        }
        flushSync(async () => {
            await setData((old) => {
                if (!old) {
                    return null
                }
                let copy = JSON.parse(JSON.stringify(old))
                let copyProducts = copy.products
                const activeIndex = copyProducts[activeContainer].findIndex((item: any) => {
                    return (item && item.id) && item.id === activeId
                })
                const myOverContainer = copyProducts[overContainer]
                const overIndex = myOverContainer.findIndex(
                    (item: any) => (item && item.id) && item.id === overId
                )

                if (!myOverContainer) {
                    copyProducts[overContainer] = []
                }

                if (activeIndex !== overIndex) {
                    copyProducts = { ...copyProducts, [overContainer]: arrayMove(myOverContainer, activeIndex, overIndex) }
                }
                let order = 0
                const originalContainer = parseInt(active?.data?.current?.originalContainer || '0')
                if (originalContainer && overContainer !== originalContainer) {
                    for (const key in copyProducts[originalContainer]) {
                        copyProducts[originalContainer][key].order = order + 1
                        order++
                    }
                }
                order = 0
                for (const key in myOverContainer) {
                    copyProducts[overContainer][key].order = order + 1
                    if ((active.id === copyProducts[overContainer][key].id)) {
                        worksheetProductWithStatus = {
                            id: copyProducts[overContainer][key].id,
                            name: copyProducts[overContainer][key].name,
                            order: copyProducts[overContainer][key].order,
                            WorkSheetProductStatusId: overContainer,
                        }
                    }
                    copyProducts[overContainer][key].originalContainer = overContainer
                    productReference = copyProducts[overContainer][key].reference
                    order++
                }
                copy.products = copyProducts
                return {
                    ...copy
                }
            })
            if (worksheetProductWithStatus) {
                const updated = await twinFetchPost(workSheetProductStatusURLs.updateProductStatus, { WorkSheetProductStatusId: overContainer, WorkSheetId: WorkSheetId ? WorkSheetId : active.data.current?.product?.WorkSheetId, typeStatus: memorizedStatuses?.[overContainer].typeStatus, id: active.data.current?.product.id })
                const responseJSON = await updated.json()
                let openModal: UseEditDeleteModalProps = null
                if (responseJSON?.errors && responseJSON?.errors?.[0].msg.includes('matchProduct')) {
                    const errorSplit = responseJSON.errors[0].msg.split('-_-')
                    const WorkSheetProductMatchId = errorSplit[1]
                    const nLine = errorSplit[3]
                    openModal = {
                        type: 'edit',
                        allRowData: { WorkSheetProductId: active.data.current?.product.id, WorkSheetProductMatchId, nLine, productReference }
                    }
                }
                if (memorizedStatuses?.[overContainer].LabelId) {
                    let extraParamsForTag: TwinDictionary = { id: active.data.current?.product.WorkSheetId, tagQuantity: active.data.current?.product.quantity, LabelId: memorizedStatuses?.[overContainer].LabelId, SpecificPriceRuleId: memorizedStatuses?.[overContainer].SpecificPriceRuleForLabelId, WorkSheetProductId: active.data.current?.product.id}
                    if (openModal?.allRowData) {
                        extraParamsForTag['extraParamsForMatch'] = openModal?.allRowData
                    }
                    openModal = { type: 'delete', allRowData: extraParamsForTag }
                }
                if (openModal) {
                    setOpenModal(openModal)
                }
                if (responseJSON) {
                    await getData()
                }
            }
        })
    }, [setData, memorizedStatuses, workSheetProductStatusURLs.updateProductStatus, getData, WorkSheetId, setOpenModal])

    const onChangeAssignedEmployee: WorkSheetProductAssignedEmployeeTy = useCallback(async (WorkSheetProductStatusId, id, posIndex, AssignedEmployeeId) => {
        const result = await twinFetchPostJSON('/api/app/order/sell/workSheet/workSheetProduct/updateWorkSheetProduct', { id, AssignedEmployeeId })
        if(result){
            setData((old)=>{
                if(!old){
                    return null
                }
                const copyOld = JSON.parse(JSON.stringify(old))
                if (copyOld.products?.[WorkSheetProductStatusId][posIndex]){
                    const product = copyOld.products?.[WorkSheetProductStatusId][posIndex]
                    if(product.id === parseInt(String(id))){
                        product['AssignedEmployee.profile_image']= result.AssignedEmployee.profile_image
                        product['AssignedEmployeeId'] = result.AssignedEmployeeId
                    }
                }
                return {
                    ...copyOld
                }
            })
        }
    }, [setData])

    return { sensors, handleOnDragEnd, onHandleDragOver, openModal, setOpenModal, memorizedGroup, memorizedArrStatuses, onChangeAssignedEmployee }
}

interface DroppableStatusColumnProps {
    id: string
    status: WorkSheetProductStatusModelType
    products: TwinDictionary | null
    setOpenModal: SetEditDeleteModalTy
    onChangeAssignedEmployee: WorkSheetProductAssignedEmployeeTy
    showAllWorkSheets: boolean
    readOnly?: boolean
}

const DroppableStatusColumn: React.FC<DroppableStatusColumnProps> = ({ id, status, products, setOpenModal, showAllWorkSheets, onChangeAssignedEmployee, readOnly }) => {
    const { setNodeRef } = useSortable({
        id,
        data: {
            columnId: id
        }
    })
    
    const renderThis: JSX.Element[] = []
    let totalQuantity = 0
    if (products && Object.keys(products).length) {
        for (const key in products || {}) {
            const item = products[parseInt(key)]
            totalQuantity += parseFloat(item.quantity)
            renderThis.push(
                <DraggableProduct product={item} id={item?.id} index={item?.id} key={item?.id} originalContainer={item?.originalContainer} order={item?.order} posIndex={parseInt(key)} {...{ showAllWorkSheets, setOpenModal, readOnly, onChangeAssignedEmployee }} />
            )
        }
    }
    const statusColor = status.color
    return (
        <SortableContext items={products as any || {}} strategy={rectSortingStrategy} id={id}>
            <div className='flex flex-col mr-15'>
                <div className='py-12 px-20 bg-gray-F7 border-t-4' style={{ borderColor: statusColor }}>
                    <div className='flex items-center gap-2'>
                        <h2 className='text-gray-51 text-18'>{status.name}</h2>
                        <div className='flex items-center justify-center h-20 min-w-20 px-5 ml-auto bg-gray-DD rounded-full light12 '>{displayFormatedNumber(totalQuantity)}</div>
                    </div>
                </div>
                <div className='flex flex-col flex-auto bg-gray-F7 mt-10 px-10 pt-5 pb-15'>
                    <div ref={setNodeRef} style={{ minHeight: '500px' }}>
                        {renderThis}
                    </div>
                </div>
            </div>
        </SortableContext>
    )
}


interface DraggableProductProps {
    id: string
    product: WorkSheetProduct
    index: string
    posIndex: number
    originalContainer: number
    order: number
    setOpenModal: SetEditDeleteModalTy
    showAllWorkSheets: boolean
    onChangeAssignedEmployee: WorkSheetProductAssignedEmployeeTy
    readOnly?: boolean
}

const DraggableProduct: React.FC<DraggableProductProps> = ({ id, product, index, originalContainer, setOpenModal, showAllWorkSheets, readOnly, order, posIndex, onChangeAssignedEmployee }) => {
    const { attributes, listeners, setNodeRef, transform } = useSortable({
        id,
        data: {
            productId: id,
            index,
            product,
            originalContainer
        },
    });
    const style = {
        transform: CSS.Translate.toString(transform),
    }
    const productName = product?.name
    const showImageWorkSheetCard = getConfigParam('showImageWorkSheetCard') === 'on'
    const showDocDescriptionWorkSheetCard = getConfigParam('showDocDescriptionWorkSheetCard') === 'on'
    const showNotesWorkSheetCard = getConfigParam('showNotesWorkSheetCard') === 'on'
    let myProps = {}
    if (!product.notModificable && !readOnly) {
        myProps = {
            onDoubleClick: () => setOpenModal({
                type: 'create', allRowData: { product, WorkSheetProductStatusId: originalContainer, order: order}
            }),
            ...listeners,
            ...attributes
        }
    }
    let extraStylesText: TwinDictionary = {}
    const productColor = product['Product.featuredColor'] || product['Product.Categories.worksheetColor']
    if(productColor){
        extraStylesText.color = productColor
    }
    const idForTooltip = String(product.id)
    const employees = getEmployees()
    return (
        <div ref={setNodeRef} style={style} className='mt-8' {...myProps}>
            <div className='flex flex-col gap-2 rounded-lg bg-white px-10 py-5 product_status_flow_card_content'>
                <div className='flex items-center'>
                    <div className='overflow-hidden twin_elipsis flex flex-auto w-1' data-tooltip-id={'ref' + idForTooltip}>
                        <span className='text-gray-51 twin_elipsis mr-2  flex-auto w-1 regular12 mt-1' style={extraStylesText}>{productName}</span>
                        <div className='flex items-center justify-center h-20 min-w-20 px-4 ml-auto bg-gray-DD rounded-full light12 '> {displayFormatedNumber(product?.quantity)} </div>
                    </div>
                    <ChangeQuantityButton id={idForTooltip + 'quantity'} allRowData={product} className='w-14 h-14' setOpenModal={setOpenModal}/>
                </div>
                {showImageWorkSheetCard && product?.['Product.ProductImages.url'] ?
                    <ITDisplayImage value={product?.['Product.ProductImages.url']} allRowData={product} className='h-35 w-35 mb-5' />
                    : null}
                <TwinToolTip id={'ref' + idForTooltip} place='top' content={product?.reference + ' - ' + product?.name} />
                {showAllWorkSheets ?
                    <Fragment>
                        <div>
                            <span className='text-green-43 mr-5'>#{product?.['WorkSheet.full_assigned_number']}</span>
                            <span className='text-gray-8F mr-5 twin_elipsis'>{product?.['WorkSheet.Customer.fiscal_name'] || product?.['WorkSheet.Customer.name'] || ''}</span>
                        </div>
                        <div>
                            <span className='text-gray-8F'>{product?.['WorkSheet.custom_name']}</span>
                        </div>
                    </Fragment>
                    : null}
                <span className='text-gray-8F' style={extraStylesText}>{product?.reference}</span>
                {showDocDescriptionWorkSheetCard ?
                <Fragment>
                    <TwinToolTip id={'img'+idForTooltip} place='top' content={product.descriptionDocuments} />
                    <div className='overflow-hidden twin_elipsis flex' data-tooltip-id={'img'+idForTooltip}>
                        <span className='text-gray-8F twin_elipsis mr-5' style={extraStylesText}>{product.descriptionDocuments}</span>
                    </div>
                </Fragment>
                : null}
                {showNotesWorkSheetCard ?
                <Fragment>
                    <TwinToolTip id={'notes'+idForTooltip} place='top' content={product.notes} />
                    <div className='overflow-hidden twin_elipsis flex' data-tooltip-id={'img'+idForTooltip}>
                        <span className='text-gray-8F twin_elipsis mr-5' style={extraStylesText}>{product.notes}</span>
                    </div>
                </Fragment>
                : null}
                <div className='flex text-gray-8F items-center font-light'>
                    <TwinIcon icon={faCalendar} className='mr-10' />
                    <span>{displayDateByString(product?.['dateLastAction'] || product?.createdAt)}</span>
                    <div className='ml-auto flex'>
                        <PrintWorkSheetTagsButton id={idForTooltip} allRowData={product} setOpenModal={setOpenModal} />
                        <div className='text-gray-51 font-light flex ml-10'>
                            <CustomSelectRoundImage className='h-27 w-27 worksheet_round_image' items={employees || {}} name={product?.['AssignedEmployee.fullname_short']} value={product?.['AssignedEmployeeId']} img={product?.['AssignedEmployee.profile_image']} icon={faUser} defaultImage={'/user-png.png'} onChange={(value) => onChangeAssignedEmployee(product.WorkSheetProductStatusId, parseInt(id), posIndex, value)} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default WorkSheetProductStatusFlowInner